import { ref } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { HttpApiError } from '@backmarket/http-api/src/utils/HttpApiError'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

export function useAddToCart() {
  const idBeingAddedToCart = ref<string>()
  const addingToCart = ref<'pending' | 'idle' | 'success' | 'error'>('idle')
  const errorAddingToCart = ref<HttpApiError | null>()
  const { trackErrors } = useTracking()
  const { fetchUser } = useUserStore()
  const $httpFetch = useHttpFetch()

  async function runCartCall(call: () => Promise<unknown>) {
    addingToCart.value = 'pending'
    errorAddingToCart.value = null

    try {
      await call()
      addingToCart.value = 'success'
    } catch (error) {
      const httpError = HttpApiError.fromAnyError(error)
      addingToCart.value = 'error'
      errorAddingToCart.value = httpError
      trackErrors({
        zone: 'bundle_add_to_cart',
        message: httpError.message,
      })

      return
    }

    await fetchUser()
  }

  async function addToCart(listingId: string) {
    idBeingAddedToCart.value = listingId

    await runCartCall(() =>
      $httpFetch('/bm/cart/add_product', {
        method: 'POST',
        body: { listing_id: Number(listingId) },
      }),
    )
  }

  async function addAllToCart(listingIds: string[]) {
    await runCartCall(() =>
      $httpFetch('/bm/cart/add_products', {
        method: 'POST',
        body: {
          listings: listingIds.map((id) => ({ listing_id: Number(id) })),
        },
      }),
    )
  }

  async function addFlashSalesDiscountToCart(
    listingPublicId: string,
    discountCode: string,
  ) {
    await $httpFetch('/bm/cart/services', {
      method: 'POST',
      body: {
        type: 'PROMO_CODE',
        listingPublicId,
        promoCodeToken: discountCode,
      },
    })
  }

  async function addPartnerPromoCodeToCart(
    listingPublicId: string,
    partnerPromoCode: GetProductResponse['includedServiceOffers']['partnerPromoCodes'][number],
  ) {
    await $httpFetch('/bm/cart/services', {
      method: 'POST',
      body: {
        type: 'PARTNER_PROMO_CODE',
        listingPublicId,
        offerId: partnerPromoCode.id,
        partnerName: partnerPromoCode.partnerName,
      },
    })
  }

  async function addMobilePlanToCart(
    listingPublicId: string,
    mobilePlanOfferId: string,
  ) {
    await $httpFetch('/bm/cart/services', {
      method: 'POST',
      body: {
        type: 'MOBILE_PLAN',
        listingPublicId,
        offerId: mobilePlanOfferId,
      },
    })
  }

  return {
    addToCart,
    addAllToCart,
    idBeingAddedToCart,
    addingToCart,
    errorAddingToCart,
    addFlashSalesDiscountToCart,
    addMobilePlanToCart,
    addPartnerPromoCodeToCart,
  }
}
